'use client';

import React, { useState } from 'react';

import {
    Box,
    BoxContent,
    Icon,
    IconName,
    Modal,
    ModalBody,
    RadioTile,
    RadioTiles,
    Row,
    Spacer,
    SpacerSize,
    Text,
    TextLevels,
    TextMarkup,
    Title,
    TitleLevels,
    TrilogyColor,
    TypographyBold,
} from '@bytel/trilogy-react';

import { filterPromotionsByTypesMarketing } from '@services/products';

import { MobilePlanType } from '@app-types/plan';

import { InlinePrice } from '@components/inline-price';

import { MarketingsState, PartialMarketingsState } from '@hooks/use-marketings';

import { MarketingType } from '@constants/plan';

import { sendProductPortabilityTracking, updatePromos } from '@product-detail/helpers';

export type PromoPortabilityProps = {
    currentPlan?: MobilePlanType;
    setMarketings: (marketings: PartialMarketingsState) => MarketingsState;
    marketings: MarketingsState;
    isRenewal: boolean;
};
export function PromoPortability({ currentPlan, marketings, setMarketings, isRenewal }: PromoPortabilityProps) {
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

    const updatePromo = updatePromos(marketings, setMarketings, sendProductPortabilityTracking, isRenewal);

    if (!currentPlan) return null;

    const promos = filterPromotionsByTypesMarketing(
        currentPlan.details?.discounts || [],
        [MarketingType.PORTABILITY],
        true,
        true,
    );
    const price = promos.reduce((acc, promotion) => acc + promotion.value, 0);
    const duration = promos.reduce((acc, promotion) => Math.max(acc, promotion.duration), 0);

    if (promos.length === 0) return null;
    return (
        <Row>
            <Text level={TextLevels.ONE} typo={TypographyBold.TEXT_WEIGHT_SEMIBOLD}>
                Vous voulez conserver votre numéro&nbsp;?
                <Icon name={IconName.INFOS_CIRCLE} onClick={() => setIsModalOpen(true)} className='cursor-pointer' />
            </Text>
            <Text>
                C&apos;est possible, avec en plus,&nbsp;
                {price > 0 ? (
                    <>
                        une remise automatique de&nbsp;
                        <InlinePrice amount={-price} period='mois' />
                    </>
                ) : (
                    'des prix exclusifs'
                )}
                &nbsp;pendant {duration} mois sur mon forfait.
            </Text>
            <Spacer size={SpacerSize.FOUR} />
            <RadioTiles className='grid-radio-list is-marginless'>
                <RadioTile
                    id='portability-yes'
                    name='portability'
                    horizontal
                    disabled={isRenewal}
                    label='Oui'
                    onChange={() => updatePromo(true, [MarketingType.PORTABILITY])}
                    checked={marketings[MarketingType.PORTABILITY]}
                />
                <RadioTile
                    id='portability-no'
                    name='portability'
                    horizontal
                    disabled={isRenewal}
                    label='Non'
                    onChange={() => updatePromo(false, [MarketingType.PORTABILITY])}
                    checked={!marketings[MarketingType.PORTABILITY]}
                />
            </RadioTiles>
            <Modal active={isModalOpen} onClose={() => setIsModalOpen(false)} title='Je garde mon numéro'>
                <ModalBody>
                    <Text markup={TextMarkup.SPAN}>et profite ainsi de&nbsp;</Text>
                    <InlinePrice amount={-price} period='mois' />
                    <Text markup={TextMarkup.SPAN}>&nbsp;de remise sur mon forfait pendant {duration} mois.</Text>

                    <Spacer size={SpacerSize.SIX} />

                    <div>
                        <Box shadowless>
                            <BoxContent backgroundColor={TrilogyColor.NEUTRAL_FADE}>
                                <Title level={TitleLevels.FIVE}>Comment ça marche ?</Title>

                                <Text level={TextLevels.TWO} typo={TypographyBold.TEXT_WEIGHT_SEMIBOLD}>
                                    1. Je souscris un forfait Bouygues Telecom 150Go et plus
                                </Text>

                                <Text level={TextLevels.TWO} typo={TypographyBold.TEXT_WEIGHT_SEMIBOLD}>
                                    2. A l&apos;étape du panier, je saisis mon numéro RIO et finalise ma commande
                                </Text>
                                <Text level={TextLevels.TWO}>
                                    J&apos;appelle le 3179 depuis mon mobile (numéro gratuit) pour le recevoir par SMS.
                                </Text>

                                <Text level={TextLevels.TWO} typo={TypographyBold.TEXT_WEIGHT_SEMIBOLD}>
                                    3. J&apos;active ma nouvelle carte SIM depuis mon espace client
                                </Text>

                                <Text
                                    markup={TextMarkup.SPAN}
                                    level={TextLevels.TWO}
                                    typo={TypographyBold.TEXT_WEIGHT_SEMIBOLD}
                                >
                                    4. Je profite de la remise de&nbsp;
                                </Text>
                                <InlinePrice level={TextLevels.TWO} amount={-price} period='mois' />
                                <Text
                                    markup={TextMarkup.SPAN}
                                    level={TextLevels.TWO}
                                    typo={TypographyBold.TEXT_WEIGHT_SEMIBOLD}
                                >
                                    &nbsp;pendant {duration} mois
                                </Text>
                                <Text level={TextLevels.TWO}>
                                    La remise est appliquée automatiquement dès la première facture.
                                </Text>
                            </BoxContent>
                        </Box>
                    </div>
                </ModalBody>
            </Modal>
        </Row>
    );
}
